import { useSceneEvents } from "@/components/common/scene-events-context";
import {
  CameraAnimation,
  type CameraAnimationProps,
} from "@/components/r3f/animations/camera-animation";
import { ToPanoAnimation } from "@/components/r3f/animations/to-pano-animation";
import { useCached3DObject } from "@/object-cache";
import { isPanoObject } from "@/object-cache-type-guard";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElementOrthomosaic,
  isIElementOrthomosaic,
} from "@/types/ielement-extra-types";
import { CameraView, CameraViewType } from "@/utils/cam-view";
import { selectPanoCameraTransform } from "@/utils/camera-transform";
import {
  IElementsState,
  selectIElementWorldPosition,
  useNonExhaustiveEffect,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  isIElementGaussianSplats,
  isIElementGenericStream,
  isIElementImg2d,
  isIElementImg360,
  type IElement,
  type IElementGenericImgSheet,
  type IElementGenericStream,
  type IElementImg2d,
} from "@faro-lotv/ielement-types";
import type { Vector3 as Vector3Prop } from "@react-three/fiber";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type JSX,
} from "react";
import { Matrix4, Object3D, Quaternion, Vector3, Vector3Tuple } from "three";
import { ModelToModel } from "./animations/model-to-model";
import { ModelToPano } from "./animations/model-to-pano";
import { PanoToModel } from "./animations/pano-to-model";
import { PanoToPano } from "./animations/pano-to-pano/pano-to-pano";
import { WalkSceneEventsPostman } from "./walk-scene-events-postman";
import { ViewType, type WalkSceneActiveElement } from "./walk-types";

type WalkSceneAnimationProp = {
  /** The element from which we start the animation */
  currentElement: WalkSceneActiveElement;

  /** The target element of the animation */
  targetElement: WalkSceneActiveElement;

  /** if provided quaternion should be applied while performing pano animation */
  targetQuaternion?: Quaternion;

  /** sheet used to get the elevation of the pano */
  sheetForElevation?: IElementGenericImgSheet;

  /** Which view is this: main view in walk mode, left view, or right view in splitscreen mode */
  viewType: ViewType;

  /** Object responsible to deliver lookAt and setCameraView events */
  eventsPostman?: WalkSceneEventsPostman;

  /** Callback executed when the animation finishes */
  onAnimationFinished(target: WalkSceneActiveElement): void;

  /** Callback executed when the camera moves in a different position */
  onCameraMoved?(position: Vector3): void;
};

/** @returns The correct animation for the walk scene */
export function WalkSceneAnimation({
  onAnimationFinished,
  currentElement,
  targetElement,
  eventsPostman,
  ...props
}: WalkSceneAnimationProp): JSX.Element {
  const [transitionFinished, setTransitionFinished] = useState(false);

  useDisposePanoDepths(currentElement);

  const targetPosition = useAppSelector(
    selectTargetElementPosition(targetElement, props.sheetForElevation),
    isEqual,
  );

  const [lookAtAnimation, setLookAtAnimation] =
    useState<CameraAnimationProps>();

  const onLookAt = useCallback(
    (position: Vector3) => {
      const quaternion = new Quaternion().setFromRotationMatrix(
        new Matrix4().lookAt(
          new Vector3().fromArray(targetPosition),
          position,
          Object3D.DEFAULT_UP,
        ),
      );
      setLookAtAnimation({
        quaternion,
      });
    },
    [targetPosition, setLookAtAnimation],
  );

  const onSetCameraView = useCallback(
    (cameraView: CameraView) => {
      if (cameraView.type === CameraViewType.perspective) {
        setLookAtAnimation({
          position: cameraView.position,
          quaternion: cameraView.rotation,
          fov: cameraView.zoomLevel,
        });
      }
    },
    [setLookAtAnimation],
  );

  // Hello postman. Any mail for us?
  useNonExhaustiveEffect(() => {
    const event = eventsPostman?.pop(targetElement.id);
    if (event) {
      if (event.lookAt) {
        onLookAt(event.lookAt);
      } else if (event.cameraView) {
        onSetCameraView(event.cameraView);
      }
    }
  }, []);

  const { lookAt, setCameraView } = useSceneEvents();
  useTypedEvent(lookAt, onLookAt);
  useTypedEvent(setCameraView, onSetCameraView);

  return (
    <>
      <WalkSceneTransition
        currentElement={currentElement}
        targetElement={targetElement}
        {...props}
        onAnimationFinished={() => {
          setTransitionFinished(true);
          if (!lookAtAnimation) {
            onAnimationFinished(targetElement);
          }
        }}
      />
      {/** If there's a lookAt animation scheduled, wait for the main scene transition to end first */}
      {transitionFinished && lookAtAnimation && (
        <CameraAnimation
          {...lookAtAnimation}
          onAnimationFinished={() => {
            setTransitionFinished(false);
            setLookAtAnimation(undefined);
            onAnimationFinished(targetElement);
          }}
        />
      )}
    </>
  );
}

function WalkSceneTransition({
  currentElement,
  targetElement,
  sheetForElevation,
  viewType,
  onCameraMoved,
  targetQuaternion,
  onAnimationFinished,
}: WalkSceneAnimationProp): JSX.Element | null {
  // TODO: Ensure onCameraMoved is called, in the animation components, when the camera position changes https://faro01.atlassian.net/browse/SWEB-3349
  // Gaussian splats are only rendered by the flywalk renderers, which never animate
  // through this classic walk-scene transition.
  if (
    isIElementGaussianSplats(currentElement) ||
    isIElementGaussianSplats(targetElement)
  ) {
    return null;
  }
  if (isIElementImg2d(targetElement) || isIElementOrthomosaic(targetElement)) {
    return (
      <ToImageTransition
        targetElement={targetElement}
        onAnimationFinished={onAnimationFinished}
      />
    );
  }

  if (
    isIElementImg2d(currentElement) ||
    isIElementOrthomosaic(currentElement)
  ) {
    if (isIElementImg360(targetElement)) {
      return (
        <ToPanoAnimation
          key={targetElement.id}
          panoElement={targetElement}
          sheetForElevation={sheetForElevation}
          isSecondaryView={viewType === ViewType.RightView}
          onAnimationFinished={(el) => onAnimationFinished(el.iElement)}
          duration={0.5}
        />
      );
    }
    return (
      <ImageToModel
        targetElement={targetElement}
        onAnimationFinished={onAnimationFinished}
        onCameraMoved={onCameraMoved}
      />
    );
  }

  if (isIElementImg360(currentElement)) {
    if (isIElementImg360(targetElement)) {
      /** Current: Pano, Target: Pano */
      return (
        <PanoToPano
          // The key property is necessary so that the previous animation is
          // unmounted completely before mounting the new one
          key={targetElement.id}
          currentElement={currentElement}
          targetElement={targetElement}
          targetQuaternion={targetQuaternion}
          sheetForElevation={sheetForElevation}
          isSecondaryView={viewType === ViewType.RightView}
          onCameraMoved={onCameraMoved}
          onAnimationFinished={onAnimationFinished}
        />
      );
    }
    /** Current: Pano, Target: PointCloud/CAD */
    return (
      <PanoToModel
        currentElement={currentElement}
        targetElement={targetElement}
        sheetForElevation={sheetForElevation}
        onAnimationFinished={onAnimationFinished}
        onCameraMoved={onCameraMoved}
      />
    );
  } else if (isIElementGenericStream(currentElement)) {
    if (isIElementImg360(targetElement)) {
      /** Current: PointCloud, Target: Pano */
      return (
        <ModelToPano
          currentElement={currentElement}
          targetElement={targetElement}
          sheetForElevation={sheetForElevation}
          onCameraMoved={onCameraMoved}
          onAnimationFinished={onAnimationFinished}
          isSecondaryView={viewType === ViewType.RightView}
        />
      );
    }
    /** Current: PointCloud/CAD, Target: PointCloud */
    return (
      <ModelToModel
        targetElement={targetElement}
        onAnimationFinished={onAnimationFinished}
      />
    );
  }
  return null;
}

type ToImageTransitionProp = {
  /** The target model element of the animation */
  targetElement: IElementImg2d | IElementOrthomosaic;
  /** The callback executed when the animation finishes */
  onAnimationFinished(target: IElement): void;
};

/** @returns The direct transition (fake animation) to 2D photo for the walk scene */
export function ToImageTransition({
  targetElement,
  onAnimationFinished,
}: ToImageTransitionProp): null {
  useLayoutEffect(() => {
    onAnimationFinished(targetElement);
  }, [onAnimationFinished, targetElement]);
  return null;
}

type ImageToModelProp = {
  /** The target model element of the animation */
  targetElement: IElementGenericStream;

  /** The callback executed when the animation finishes */
  onAnimationFinished(target: IElementGenericStream): void;

  /** The callback executed when the camera changes position */
  onCameraMoved?(position: Vector3Prop): void;
};

function ImageToModel({
  targetElement,
  onAnimationFinished,
}: ImageToModelProp): null {
  useLayoutEffect(() => {
    onAnimationFinished(targetElement);
  }, [onAnimationFinished, targetElement]);
  return null;
}

/**
 * When a new animation starts, we check whether the object we are animating away is a pano
 * with depth. Depth data can occupy a lot of RAM memory (128MB per pano in the Walmart project).
 * This hook ensures that depth data are deallocated the instant we animate away from a given pano.
 *
 * @param currentElement The element currently displayed in the scene
 */
function useDisposePanoDepths(currentElement: WalkSceneActiveElement): void {
  const panoObj = useCached3DObject(currentElement, "DisposePanoDepths");
  useEffect(() => {
    if (!isPanoObject(panoObj)) {
      return;
    }
    panoObj.tiledPano.disposeDepths();
  }, [panoObj]);
}

const selectTargetElementPosition = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, targetElement: WalkSceneActiveElement) =>
        targetElement,
      (
        State: OnlyIElementsStore,
        targetElement: WalkSceneActiveElement,
        sheetForElevation?: IElementGenericImgSheet,
      ) => sheetForElevation,
    ],
    computeTargetElementPosition,
  ),
);

function computeTargetElementPosition(
  iElements: IElementsState,
  targetElement: WalkSceneActiveElement,
  sheetForElevation?: IElementGenericImgSheet,
): Vector3Tuple {
  const state = { iElements };
  if (isIElementImg360(targetElement)) {
    return selectPanoCameraTransform(targetElement, sheetForElevation)(state)
      .position;
  }
  return selectIElementWorldPosition(targetElement.id)(state);
}
